import { idbDeleteDatabase, idbGetKeys } from './idb.js';

/**
 * Rückgabe von `loadPatientKeypairFromIDB`. Reduziert den IDB-Record auf das,
 * was die Submit-/Polling-Schicht braucht — `expiresAt` ist implizit gültig.
 */
interface LoadedPatientKeypair {
  readonly tenantId: string;
  readonly queueItemId: string;
  readonly publicKeyBase64: string;
  readonly privateKey: CryptoKey;
}

/**
 * Versucht, ein Patient-Keypair aus IndexedDB zu rekonstruieren.
 *
 * Verhalten:
 * - Kein Record → `null`.
 * - Record vorhanden, aber `expiresAt <= now` → IDB komplett löschen, `null`.
 * - Sonst → reduziertes Tupel.
 *
 * Der `now`-Parameter ist injizierbar, damit Tests deterministisch bleiben.
 */
export async function loadPatientKeypairFromIDB(now: number): Promise<LoadedPatientKeypair | null> {
  const record = await idbGetKeys();
  if (record === null) {
    return null;
  }
  if (record.expiresAt <= now) {
    // Cleanup-Fehler (z.B. IDB_DELETE_BLOCKED durch zweiten Tab) dürfen NICHT
    // den Recovery-Pfad als generischen Error verlassen — die DB ist effektiv
    // unbrauchbar, der Aufrufer behandelt das wie „kein Recovery vorhanden"
    // (Code-Review DEV-013, W2).
    try {
      await idbDeleteDatabase();
    } catch {
      // Bewusst verworfen: ein zweiter Tab kann die Löschung blockieren.
    }
    return null;
  }
  return {
    tenantId: record.tenantId,
    queueItemId: record.queueItemId,
    publicKeyBase64: record.publicKeyBase64,
    privateKey: record.privateKey,
  };
}
